import { DOCUMENT } from '@angular/common';
import { Component, HostBinding, Inject, OnInit, Renderer2 } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { Angulartics2, Angulartics2GoogleGlobalSiteTag } from 'angulartics2';
import { NGXLogger } from 'ngx-logger';
import { firstValueFrom } from 'rxjs';
import { AuthorizationService } from './authorization/authorization.service';
import { BodyStylingService } from './body-styling.service';
import { FeatureFlagService, IFeatures } from './core/feature-flags/feature-flag.service';
import { routerAnimations } from './router-animation';
import { EnterpriseConfigurationDataAccessService } from './shared/modules/enterprise-configuration/enterprise-configuration-data-access.service';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'body',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  animations: [routerAnimations],
})
export class AppComponent implements OnInit {
  constructor(
    authorizationService: AuthorizationService,
    angulartics: Angulartics2,
    angulartics2GoogleGlobalSiteTag: Angulartics2GoogleGlobalSiteTag,
    private _bodyStylingService: BodyStylingService,
    private _enterpriseConfigurationService: EnterpriseConfigurationDataAccessService,
    private _renderer: Renderer2,
    titleService: Title,
    @Inject(DOCUMENT) private _document: Document,
    router: Router,
    logger: NGXLogger,
    private _featureFlagService: FeatureFlagService,
  ) {
    angulartics2GoogleGlobalSiteTag.startTracking();
    authorizationService.principal$.subscribe((principal) => {
      if (principal.identity.isAuthenticated) {
        const userIdentity = principal.identity;
        if (principal.identity.idpAccountId) {
          angulartics.setUsername.next(principal.identity.idpAccountId);
        }
        //Current 'user_properties' doesn't work perfectly in GA4 with angulartics2GoogleGlobalSiteTag
        // so gtag used instead of angulartics2GoogleGlobalSiteTag
        gtag('set', 'user_properties', {
          user_type: userIdentity.userType,
          user_email_domain: userIdentity.email?.split('@')[1],
          idp_account_id: userIdentity.idpAccountId,
        });
        angulartics.eventTrack.next({
          // component-object-action
          action: 'app-user-login',
          properties: {
            category: 'app',
          },
        });
        logger.info('sdm-portal-login', { identity: { idpAccountId: userIdentity.idpAccountId } });
      }
    });
    const config$ = this._enterpriseConfigurationService.getEnterpriseConfiguration();
    config$.subscribe((conf) => titleService.setTitle(conf.siteName));

    this._featureFlagService.getFeatures().subscribe((features: IFeatures) => {
      if (features.qualified_js_chatbot?.isEnabled) {
        this.injectQualifiedJsScript();
      }
    });
    router.initialNavigation();
  }

  @HostBinding('class.no-overflow')
  public get bodyNoOverflow(): boolean {
    return this._bodyStylingService.noOverflow;
  }

  @HostBinding('class.signIn')
  public get bodySignIn(): boolean {
    return this._bodyStylingService.signIn;
  }

  async ngOnInit(): Promise<void> {
    const configuration = await firstValueFrom(this._enterpriseConfigurationService.getEnterpriseConfiguration());
    this._renderer.setAttribute(this._document.body, 'theme', configuration.theme);
  }

  injectQualifiedJsScript(){
    const inlineScript = this._renderer.createElement('script');
    inlineScript.type = 'text/javascript';
    inlineScript.text = `
      (function(w,q){w['QualifiedObject']=q;w[q]=w[q]||function(){
      (w[q].q=w[q].q||[]).push(arguments)};})(window,'qualified')
    `;
    this._renderer.appendChild(this._document.head, inlineScript);
    const externalScript = this._renderer.createElement('script');
    externalScript.src = 'https://js.qualified.com/qualified.js?token=rs5Kb7sKqkzN1qhx';
    externalScript.async = true;
    this._renderer.appendChild(document.head, externalScript);
  }
}
